import { sentences } from '@mado/schema/showcase';
import { labelText, overlap, phraseSpan } from './cue-label';
import type { Rect } from './overlays';
import { el } from './stage';
import type { Strings } from './strings';

/**
 * Callouts (founder, 2026-10-15, after the timed label tour in click-through): "Highlights in a
 * step go too fast, I didn't manage to read anything. ... it's hard to read when important text
 * is only highlighted, not separated individually to appear next to the block. On the
 * walkthrough the viewer should drive it." So in click-through each ring is lit with a card
 * beside it holding the sentence that names it, and the viewer presses Next. Nothing moves on
 * by itself. Watch, which plays itself, keeps the voice or the timed labels.
 */

/** The line cut into sentences (the same cut as a showcase's lines). */
export { sentences };

export interface CalloutText {
  /** The sentence that names the ring, or its phrase alone when no sentence does. */
  text: string;
  /** Where the phrase is in `text`, to set it in emphasis. Null: no emphasis. */
  span: [number, number] | null;
}

/**
 * Each ring's words, in the rings' order: the sentence of the line that contains its phrase
 * (case-insensitive), looked for from the last ring's sentence on, since the line tells the
 * rings in order. Two rings in one sentence each get it, with their own phrase in emphasis (one
 * thing lit at a time). No sentence has it: the phrase alone, first letter up. `rest`: the
 * sentences no ring used, for the instruction after the callouts.
 */
export function calloutTexts(
  line: string,
  phrases: readonly string[],
): { items: CalloutText[]; rest: string } {
  const ss = sentences(line);
  const used = new Set<number>();
  let last = 0;
  const items = phrases.map((p): CalloutText => {
    let k = -1;
    for (let i = last; i < ss.length && k < 0; i++) if (phraseSpan(ss[i]!, p)) k = i;
    for (let i = 0; i < last && k < 0; i++) if (phraseSpan(ss[i]!, p)) k = i;
    if (k < 0) return { text: labelText(p), span: null };
    used.add(k);
    last = k;
    return { text: ss[k]!, span: phraseSpan(ss[k]!, p) };
  });
  return { items, rest: ss.filter((_, i) => !used.has(i)).join(' ') };
}

/**
 * How a callout press came: a pointer on its button, a key, or a tap (on its lit block, or on a
 * step with no target).
 */
export type CalloutVia = 'button' | 'key' | 'tap';

/** The four-pane tile (the logo's shape) every instruction card leads with. */
export function tile(): HTMLElement {
  const mk = el('span', 'mado-tooltip-mk');
  mk.innerHTML = '<i></i><i></i><i></i><i></i>';
  return mk;
}

/**
 * A card's foot: the count (none when empty), then its buttons, Next last and to the right. The
 * callout's and the guide card's are one row (founder, 2026-10-15: "Why do we have two different
 * popup designs?").
 */
export function cardFoot(count: string, ...buttons: HTMLElement[]): HTMLElement {
  const foot = el('div', 'mado-callout-f');
  if (count) {
    const n = el('span', 'mado-tooltip-n');
    n.textContent = count;
    foot.appendChild(n);
  }
  foot.append(...buttons);
  return foot;
}

/** A card's button: one press a click; the second click of a double click is none. */
export function cardButton(cls: string, label: string, fn: (via: CalloutVia) => void): HTMLElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  b.textContent = label;
  b.addEventListener('click', (ev) => {
    ev.preventDefault();
    if (ev.detail > 1) return;
    fn(ev.detail > 0 ? 'button' : 'key');
  });
  return b;
}

/**
 * Back on a card (founder, 2026-10-15: "Users should be able to move back ... next step back or
 * forth"): the card before, or the step before on a step's first card. An icon button, quiet
 * beside Next.
 */
export function backButton(fn: (via: CalloutVia) => void, label = 'Back'): HTMLElement {
  const b = cardButton('mado-callout-back', '', fn);
  b.setAttribute('aria-label', label);
  b.innerHTML =
    '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M10 3.5 5.5 8l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  return b;
}

/** `text` into `t`, the part at `span` in bold: what to look at (a callout) or use (the instruction). */
export function boldIn(t: HTMLElement, text: string, span: [number, number] | null): void {
  if (!span) {
    t.textContent = text;
    return;
  }
  const em = el('strong', 'mado-callout-em');
  em.textContent = text.slice(span[0], span[1]);
  t.append(text.slice(0, span[0]), em, text.slice(span[1]));
}

/**
 * The control an instruction names, as a span of it: "Click Jobs to see your job ads." gives
 * "Jobs". The words after a leading Click/Tap/Choose/Select/Open/Press, up to "to"/"on"/"for"/
 * "and" or punctuation (`tipNamedControl` in @mado/schema/quality, which the player does not
 * import). Null: the line names no control that way.
 */
export function controlSpan(text: string): [number, number] | null {
  const m = /^\s*(?:click|tap|choose|select|open|press)\s+/i.exec(text);
  if (!m) return null;
  const from = m[0].length;
  const end = /\s+(?:to|on|for|and)\s+|[.,;:!?—–]|\s-\s/i.exec(text.slice(from));
  const to = from + (end ? end.index : text.length - from);
  return to > from ? [from, to] : null;
}

/**
 * The card: the instruction tooltip's own box (tile, words, an arrow at its ring), the sentence
 * in the action line's type with its phrase in bold (founder, 2026-10-15: "bold is enough", no
 * underline), and a foot with "2 / 3", Back (when there is somewhere to go back to), Skip (not on
 * the last) and Next. The step's instruction is the card after the last (overlays.ts
 * `renderTooltip`, founder, 2026-10-15: "these two designs are a bit too far apart"). Drawn in
 * the overlay's root, not in the picture: the camera only moves on a press, and the card is
 * placed once it is there.
 */
export interface CalloutCardOpts {
  item: CalloutText;
  i: number;
  /** Cards on the step: the callouts, and the instruction after them when there is one. */
  n: number;
  next: string;
  onNext(via: CalloutVia): void;
  onSkip(via: CalloutVia): void;
  /** Absent: no Back (the first card of the session's first step). */
  onBack?: ((via: CalloutVia) => void) | undefined;
  /** The last callout: no Skip, nothing is left to skip to. */
  last: boolean;
  /** Muted Watch plays the callouts itself: the count, no buttons. */
  auto?: boolean;
  /** The demo's words (absent: English). */
  s?: Strings | undefined;
}

/** A callout's foot: "2 / 3", Back, Skip, Next (the count alone when Watch plays itself). */
export function calloutFoot(o: CalloutCardOpts): HTMLElement {
  if (o.auto) return cardFoot(`${o.i} / ${o.n}`);
  const next = cardButton('mado-end-again mado-callout-next', o.next, o.onNext);
  const back = o.onBack ? [backButton(o.onBack, o.s?.back)] : [];
  const skip = o.last ? [] : [cardButton('mado-callout-skip', o.s?.skip ?? 'Skip', o.onSkip)];
  return cardFoot(`${o.i} / ${o.n}`, ...back, ...skip, next);
}

export function calloutCard(o: CalloutCardOpts): HTMLElement {
  const card = el('div', 'mado-tooltip mado-tooltip--say mado-callout');
  card.setAttribute('role', 'group');
  card.setAttribute('aria-label', o.s ? o.s.highlight(o.i, o.n) : `Highlight ${o.i} of ${o.n}`);
  const body = el('span', 'mado-tooltip-b');
  const t = el('span', 'mado-tooltip-t mado-callout-t');
  boldIn(t, o.item.text, o.item.span);
  body.append(t, calloutFoot(o));
  card.append(tile(), body);
  return card;
}

/** Which side of its ring a card sits on, named as the tooltip's arrow classes are. */
export type CardSide = 'below' | 'above' | 'right' | 'left';

/**
 * Where a callout's card goes, in the px of `ring`: close beside it (`gap` leaves room for the
 * arrow), on the side and at the alignment that covers least of the picture's words (`ink`,
 * `Asset.ink` projected) and of `avoid` (the targets), inside `area`. Cards covered the page
 * text they sat by (founder's screenshots, 2026-10-15: "Needs Attention", a job card's title,
 * the breadcrumb). The card shares 44 px of its ring's span along the arrow's edge, so the arrow
 * points at the ring. `side` null: in a big ring's own corner, no arrow. Null: nothing fits.
 */
export function placeCallout(
  ring: Rect,
  size: { w: number; h: number },
  area: Rect,
  avoid: readonly Rect[],
  ink: readonly Rect[],
  gap = 14,
): { x: number; y: number; side: CardSide | null } | null {
  const { w, h } = size;
  const cx = (x: number): number => Math.max(area.x, Math.min(x, area.x + area.w - w));
  const cy = (y: number): number => Math.max(area.y, Math.min(y, area.y + area.h - h));
  const cands: { x: number; y: number; side: CardSide | null }[] = [];
  for (const side of ['below', 'above'] as const) {
    const y = side === 'below' ? ring.y + ring.h + gap : ring.y - gap - h;
    if (y < area.y - 0.5 || y + h > area.y + area.h + 0.5) continue;
    for (const x of [ring.x + ring.w / 2 - w / 2, ring.x, ring.x + ring.w - w]) {
      cands.push({ x: cx(x), y, side });
    }
  }
  for (const side of ['right', 'left'] as const) {
    const x = side === 'right' ? ring.x + ring.w + gap : ring.x - gap - w;
    if (x < area.x - 0.5 || x + w > area.x + area.w + 0.5) continue;
    for (const y of [ring.y + ring.h / 2 - h / 2, ring.y, ring.y + ring.h - h]) {
      cands.push({ x, y: cy(y), side });
    }
  }
  // A ring round a whole panel (Talentika s3, the editor's canvas) leaves no room outside but
  // the toolbar's words: its own empty corners are then better, with no arrow (it is inside).
  const big = ring.w >= w + 32 && ring.h >= h + 32;
  if (big) {
    for (const y of [ring.y + ring.h - h - 16, ring.y + 16]) {
      for (const x of [ring.x + ring.w - w - 16, ring.x + 16])
        cands.push({ x: cx(x), y: cy(y), side: null });
    }
  }
  let best: { x: number; y: number; side: CardSide | null } | null = null;
  let bestCost = Infinity;
  for (const c of cands) {
    const across = c.side === 'below' || c.side === 'above';
    const shared = across
      ? Math.min(c.x + w, ring.x + ring.w) - Math.max(c.x, ring.x)
      : Math.min(c.y + h, ring.y + ring.h) - Math.max(c.y, ring.y);
    if (c.side && shared < Math.min(44, across ? ring.w : ring.h)) continue;
    const r = { x: c.x, y: c.y, w, h };
    // A target covered weighs more than words: the control must stay seen and pressable. Inside,
    // the card covers some of what it speaks of: it counts as 15% of the card in words, so an
    // outside place wins unless it covers more (Talentika s1 callout 2: a card border's 7% above
    // the ring beats its rows; s3 callout 1: the toolbar's words above lose to the canvas corner).
    const cost = overlap(r, avoid) * 4 + overlap(r, ink) + (c.side ? 0 : w * h * 0.15);
    // Ties keep the first: below, then above, then beside.
    if (cost < bestCost - 0.5) {
      best = c;
      bestCost = cost;
    }
  }
  return best;
}
